import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Siren, MapPin, Clock, AlertTriangle, HeartHandshake } from "lucide-react";
import { api } from "../lib/api";

const STATUS_TONE = {
  "Needs Help":        "bg-red-600 text-white",
  "Rescue Underway":   "bg-amber-500 text-zinc-900",
  "Partially Safe":    "bg-amber-200 text-amber-900",
  "All Rescued":       "bg-green-700 text-white",
  "Closed":            "bg-zinc-300 text-zinc-800",
};

export default function UrgentRescues() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get(`/urgent-rescues${statusFilter ? `?status=${encodeURIComponent(statusFilter)}` : ""}`);
        if (active) setItems(data.items || []);
      } catch { if (active) setItems([]); }
      finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, [statusFilter]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10" data-testid="urgent-rescues-page">
      <Helmet>
        <title>Urgent Rescue Alerts — Paws on Record</title>
        <meta name="description" content="Animals in urgent need of rescue, fosters, and volunteers. Share, help, or donate directly to verified rescue organizations."/>
      </Helmet>

      <div className="border-l-4 border-red-600 pl-5 mb-8">
        <div className="label-eyebrow text-red-600 inline-flex items-center gap-2">
          <Siren size={12}/> Urgent Rescue Alerts
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2 leading-none">
          Animals that need help right now.
        </h1>
        <p className="mt-3 text-sm sm:text-base text-zinc-700 max-w-2xl leading-relaxed">
          Paws on Record publishes rescue alerts so animals in danger get in front of rescues, fosters,
          transporters, and donors fast. Every alert is reviewed before publication. Reports that are
          not independently verified are clearly labeled as <b>Reported — Unconfirmed</b>.
        </p>
      </div>

      <div className="flex items-center flex-wrap gap-2 mb-6">
        <button
          onClick={() => setStatusFilter("")}
          className={`text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border ${statusFilter === "" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
          data-testid="urgent-filter-all"
        >
          All
        </button>
        {Object.keys(STATUS_TONE).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`text-[11px] uppercase tracking-widest font-bold px-3 py-1.5 border ${statusFilter === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 text-zinc-700 hover:border-zinc-900"}`}
            data-testid={`urgent-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Loading rescue alerts…</div>
      ) : items.length === 0 ? (
        <div className="border border-zinc-200 bg-zinc-50 p-8 text-center">
          <HeartHandshake size={28} className="mx-auto text-zinc-400 mb-3"/>
          <p className="text-sm text-zinc-600">
            No active rescue alerts right now — and that's good news.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((r) => (
            <Link
              key={r.id}
              to={`/urgent-rescues/${r.id}`}
              className="group border border-zinc-200 hover:border-zinc-900 bg-white transition flex flex-col"
              data-testid={`urgent-card-${r.id}`}
            >
              <div className="aspect-video bg-zinc-100 border-b border-zinc-200 overflow-hidden">
                {r.photo_url ? (
                  <img
                    src={r.photo_url.startsWith("/api/") ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}${r.photo_url}` : r.photo_url}
                    alt=""
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-300">
                    <Siren size={32}/>
                  </div>
                )}
              </div>
              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 ${STATUS_TONE[r.status] || "bg-zinc-100 text-zinc-800"}`}>
                    {r.status}
                  </span>
                  {(r.verification || "").startsWith("Reported") && (
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-700 inline-flex items-center gap-1">
                      <AlertTriangle size={10}/> Unconfirmed
                    </span>
                  )}
                </div>
                <h3 className="font-display font-black text-lg leading-tight group-hover:text-red-600 transition-colors">
                  {r.title}
                </h3>
                <div className="text-[11px] text-zinc-500 mt-1 inline-flex items-center gap-1 font-mono">
                  <MapPin size={10}/> {r.city}, {r.state_code || r.state}
                  {r.incident_date && <><span className="mx-1">·</span><Clock size={10}/> {r.incident_date}</>}
                </div>
                <p className="text-sm text-zinc-700 mt-2 line-clamp-3 flex-1">{r.summary}</p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
